import urllib from 'url-lib';
import { initialize } from 'redux-form';
import { has, isEmpty } from 'lodash';

import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK } from '@eb/social-login-facebook';
import {
    LOOK_UP_FORM,
    SOCIAL_SIGN_UP_FORM,
    BASE_URL,
    PASSWORD_SET_DESTINATION,
    PASSWORD_SET_TOKEN,
    PASSWORD_MUST_LINK_ACCOUNT,
    INVALID_RESET_TOKEN,
    SET_PASSWORD_FORM,
    GA_SET_PASSWORD_EVENT,
    GA_RESET_PASSWORD_EVENT,
    RESET_PASSWORD_PATH,
    TRIGGER_RESET_PASSWORD,
} from '../constants';
import { getEmailFromState } from '../actions/auth';

export const getSetPasswordFormType = (routeFormType, query) =>
    !has(query, PASSWORD_SET_TOKEN) ? TRIGGER_RESET_PASSWORD : routeFormType;

export const normalizePath = (path) => `/${path.replace(/\//g, '')}`;

/*
    This function acts as a gateway to ensure that the user has had their
    email status checked before accessing /login or /signup forms specifically.

    They can access these potentially with corrupted email states if navigating
    back and forward with the browser while modifying the email held
    within the form.
*/
export const accountStatusChecked = ({ getState }, replace) => {
    const { authentication: { isAccountStatusChecked } = {} } = getState();

    /*
     * If email does not exist within the store redirect to start of the
     * signin flow (they arrived via direct route or refresh)
     */

    if (!isAccountStatusChecked) {
        replace(BASE_URL);
    }
};

export const checkEmailExists = ({ getState }, replace) => {
    const email = getEmailFromState(getState);

    /*
     * If email was not provided, return to login page.
     * It does not check whether the e-mail is confirmed or not
     * Only that it's provided
     */
    if (!email) {
        replace(BASE_URL);
    }
};

/**
 * Every time we return to /signin we want to re-initialize the form
 * so that error states are cleared etc.
 */
export const setUpForm = ({ getState, dispatch }) => {
    const {
        authentication: { email, shouldIncludeEmailConfirmation } = {},
    } = getState();

    if (shouldIncludeEmailConfirmation) {
        // Avoid to set email as part of our experiment
        dispatch(initialize(LOOK_UP_FORM));
    } else if (email) {
        dispatch(initialize(LOOK_UP_FORM, { email }));
    }
};

export const setUpSocialForm = ({ getState, dispatch }, replace) => {
    const {
        authentication: {
            socialData: { firstName, lastName, email },
        },
    } = getState();

    // If there's no email, go back to the first step in the flow
    if (!email) {
        replace(BASE_URL);
    }

    dispatch(initialize(SOCIAL_SIGN_UP_FORM, { email, firstName, lastName }));
};

export const checkPasswordReset = ({ dispatch }, query, replace) => {
    if (has(query, INVALID_RESET_TOKEN)) {
        replace(
            urllib.formatUrl(BASE_URL, {
                [INVALID_RESET_TOKEN]: 1,
            }),
        );
    } else if (has(query, PASSWORD_SET_TOKEN)) {
        dispatch(
            initialize(SET_PASSWORD_FORM, {
                [PASSWORD_SET_TOKEN]: query[PASSWORD_SET_TOKEN],
                [PASSWORD_MUST_LINK_ACCOUNT]: query[PASSWORD_MUST_LINK_ACCOUNT],
                [PASSWORD_SET_DESTINATION]: query[PASSWORD_SET_DESTINATION],
            }),
        );
    } else if (has(query, 'email')) {
        dispatch(initialize(LOOK_UP_FORM, { email: query.email }));
    }
};

export const isFacebookLoginEnabled = (socialSSOSettings) =>
    !isEmpty(socialSSOSettings) &&
    !isEmpty(socialSSOSettings[SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK]);

export const isLoginProviderEnabled = (socialSSOSettings, provider) =>
    !isEmpty(socialSSOSettings) && !isEmpty(socialSSOSettings[provider]);

/*********************** GA TRACKING UTILS ***********************/
//Helper to determine if action should be ResetPassword or SetPassword
//The only determining factor is the current route as the same form
//is rendered in either case.
export const getSetPasswordEvent = (currentRoute) => {
    let event = GA_SET_PASSWORD_EVENT;

    if (currentRoute.indexOf(RESET_PASSWORD_PATH) > -1) {
        event = GA_RESET_PASSWORD_EVENT;
    }

    return event;
};

/**
 * Checks if it is possible to mock the call to external social login options.
 * It uses an object defined in the browser console to update the state and redirect to
 * the choosen url using react router to prevent losing the app store.
 * Social logins mocked (add new ones in the list below):
 *  * SIWA / Apple
 *
 * This is meant to be used only in DEVELOPMENT mode and once a proper mock library is made
 * it is the faster way we have to test.
 *
 * An example of the object you can use is the following:
 * window.authenticationData = {
 *     skipTo: '',
 *     idToken: 'idToken',
 *     isEmailHidden: false | true,
 *     isUser: false | true,
 *     expirationTimestamp: '2020-03-09T20:17:32Z',
 *     email: 'email@something.com',
 *     externalIdentities: {
 *         sign_in_methods: [],
 *         user_id: '1234',
 *         is_email_verified: false | true,
 *         exists: false | true,
 *         can_login: false | true,
 *     },
 * }
 *
 * @param {function} callback
 *
 */
export const checkSocialMocks = ({ callback } = {}) => {
    if (
        process.env.NODE_ENV === 'development' &&
        window.authenticationData &&
        window.authenticationData.skipTo !== undefined
    ) {
        if (AppleID && callback) {
            // Prevents the AppleID library to launch javascript its default action
            AppleID.auth.signIn = () => {
                // Execute the callback, must be an action that triggers the redux flow to update the store
                callback({
                    url: window.authenticationData.skipTo,
                    authenticationData: window.authenticationData,
                });
            };
        }
    }
};

export const notifyParent = (messageName, additionalData) => {
    const parent = window.parent;

    if (window.location.origin === parent.location.origin) {
        parent.postMessage(
            { ...additionalData, messageName },
            parent.location.origin,
        );
    }
};
